import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { openClickToDial } from "data/user/actions";
import css from "./ReadyToCall.module.scss";
import CallIcon from "../../assets/images/CallIcon.svg";
import DncIcon from "../../assets/images/DNC.svg";
import Tooltip from "../../app/components/Tooltip/index";
import DNCTooltip from "../../app/components/DNCTooltip";
import info from "../../assets/images/infogray.svg";
import greentick from "assets/images/greentick.svg";
import ProgressBar from "../../app/Campaigns/Dashboard/ProgressBar";
import classNames from "classnames";
import { MAX_DAILY_CALLS } from "../../data/user/constants";
import CallGoalReached from "../../app/AccountNew/modals/CallGoalReached";
import Loader from '../Loader';

const ReadyToCall = ({
    name,
    phone,
    initials,
    litigatorFlag,
    myDnc,
    publicDnc,
    onCancel,
    onCallNow,
    callCountForToday = 0,
    callCountForTodayLoading = false,
    isContactLoading = false,
    maxCalls = MAX_DAILY_CALLS,
}) => {
    const dispatch = useDispatch();

    const showDnc = myDnc || publicDnc;

    const progress = Math.min((callCountForToday / maxCalls) * 100, 100);
    const isMaxReached = callCountForToday >= maxCalls;
    const isNearLimit = callCountForToday >= 135 && !isMaxReached;
    const showtick = callCountForToday >= maxCalls;
    const [showGoalModal, setShowGoalModal] = useState(false);

    const handleCallClick = () => {
        // PROPS-3840: this contact's DNC / litigator status isn't known until its details load
        if (isContactLoading) return;

        if (isMaxReached) {
            setShowGoalModal(true);
            return;
        }

        onCallNow();
    };

    return (
        <div className={css.toastMsg}>
            <h2 className={css.title}>Ready to Call?</h2>

            <div className={css.contactCard}>
                <div className={css.contactTop}>
                    <div className={css.avatar}>
                        <span className={css.initials}>{initials}</span>
                    </div>

                    <div className={css.contactInfo}>
                        <div className={css.name}>{name}</div>

                        {litigatorFlag &&
                            <Tooltip title="Litigator"
                                description="A litigator is an individual who may be associated with legal actions, such as filing lawsuits related to telemarketing or consumer protection claims."
                                position="top"
                                width={312}
                                arrowOffset={30}
                                yOffset={-142}
                                xOffset={124}
                                bgColor="#128292"
                            >
                                <span className={css.litigator}>Litigator</span>
                            </Tooltip>
                        }
                        <div className={css.phone}>{phone}</div>
                    </div>
                </div>

                {showDnc && <div className={css.cardDivider} />}

                {showDnc && (
                    <div className={css.contactMeta}>
                        <DNCTooltip publicDnc={publicDnc} myDnc={myDnc}>
                            <div className={css.dncTrigger}>
                                <img src={DncIcon} alt="DNC" />
                                <span className={css.dncText}>
                                    Do Not Call (DNC)
                                </span>
                            </div>
                        </DNCTooltip>
                    </div>
                )}
            </div>

            <div className={css.tcpCard}>
                <div className={css.contactInfo}>
                    <div className={css.label}>
                        <Tooltip
                            title="Call Progress"
                            description={
                                <div style={{fontWeight: 500, lineHeight: "150%"}}>
                                    Outbound calling includes up to <br />
                                    <span style={{fontWeight: 700}}>{maxCalls} calls</span> per day to support <br />
                                    connection quality and number <br />
                                    reputation.
                                </div>
                            }
                            position="left"
                            width={272}
                            arrowOffset={30}
                            yOffset={50}
                            xOffset={214}
                            bgColor="#128292"
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <div className={css.name}>
                                    Today's Call Progress
                                </div>
                                <img src={info} alt="info" />
                            </div>
                        </Tooltip>
                    </div>

                    <div className={css.callsMadeSection}>
                        {callCountForTodayLoading
                        ? <Loader loaderSize={'small'} loaderDirection={'minHorizontal'} />
                        : <>
                            <div className={css.callModeWrapper}>
                                <div className={css.callMode}>
                                    {!isNearLimit && (
                                        <span>Calls Made:</span>
                                    )}

                                    <span className={classNames(css.callModeVal, { [css.maxReached]: callCountForToday >= maxCalls })}>
                                        &nbsp;{callCountForToday}/{maxCalls}
                                    </span>
                                    {isNearLimit &&
                                        <span> Connected Calls</span>
                                    }
                                </div>
                                {showtick && (
                                    <div>
                                        <img src={greentick} alt="Green Tick" />
                                    </div>
                                )}
                            </div>

                            <ProgressBar value={progress} showHeader={false} isSmallSize={true} />

                            {isNearLimit && (
                                <span className={css.remaining}>
                                    {Math.max(maxCalls - callCountForToday, 0)} Remaining
                                </span>
                            )}

                            {isNearLimit && (
                                <>
                                    <span className={css.tcpNote}>
                                        Need higher volume? BatchDialer adds <br /> multi-line dialing + more capacity.
                                    </span>
                                    <div className={css.upgradeWrapper}>
                                        <button className={css.upgradeBtn} onClick={() => window.open("https://batchdialer.com/click2dial", "_blank", "noopener,noreferrer")}>
                                            <span className={css.upgradeBtnLabel}>Upgrade to BatchDialer</span>
                                        </button>
                                    </div>
                                </>
                            )}
                        </>}
                    </div>
                </div>
            </div>

            <p className={css.description}>
                Ensure microphone and speakers of your device are working.
                <br />
                <span className={css.link} onClick={() => window.open("https://networktest.twilio.com/", "_blank", "noopener,noreferrer")}>
                    Click here
                </span>{" "}
                to check your audio and internet connection
            </p>

            <div className={css.actions}>
                <button className={css.cancelBtn} onClick={onCancel}>
                    Cancel
                </button>

                <button
                    className={classNames(css.callBtn, { [css.callBtnLoading]: isContactLoading })}
                    onClick={handleCallClick}
                    disabled={isContactLoading}
                    aria-busy={isContactLoading}
                >
                    <img src={CallIcon} alt="Call" />
                    <span className={css.callText}>Call Now</span>
                    {isContactLoading && (
                        <span className={css.callLoader}>
                            <Loader loaderSize={'small'} loaderDirection={'minHorizontal'} />
                        </span>
                    )}
                </button>
            </div>

            <CallGoalReached
                isOpen={showGoalModal}
                onClose={() => {
                    setShowGoalModal(false);
                    dispatch(openClickToDial(false));
                }}
            />
        </div>
    );
};

export default ReadyToCall;
